import type { JSONSchema7 } from 'json-schema';

import type { DeepReadonly } from './types';
// We want these to match the API schema as closely as possible

export const mistralJsonSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    model: {
      type: ['string', 'null'],
      description: 'ID of the model to use. Refer to the List Available Models API for model descriptions.',
      minLength: 1,
    },
    temperature: {
      type: ['number', 'null'],
      description:
        'Sampling temperature between 0.0 and 0.7. Higher values make the output more random, while lower values make it more focused and deterministic.',
      minimum: 0,
      maximum: 0.7,
    },
    top_p: {
      type: 'number',
      description:
        'Nucleus sampling parameter, where the model considers the results of the tokens with top_p probability mass.',
      default: 1,
      minimum: 0,
      maximum: 1,
    },
    max_tokens: {
      type: ['integer', 'null'],
      description:
        "The maximum number of tokens to generate in the completion. Input tokens plus max_tokens cannot exceed the model's context length.",
    },
    stream: {
      type: 'boolean',
      description: 'Whether to stream back partial progress.',
      default: false,
    },
    stop: {
      type: ['string', 'array', 'null'],
      description: 'Stop generation if this token or any token in the array is detected.',
      items: {
        type: 'string',
      },
    },
    random_seed: {
      type: ['integer', 'null'],
      description: 'The seed to use for random sampling. If set, different calls will generate deterministic results.',
    },
    messages: {
      type: 'array',
      description:
        'The prompt(s) to generate completions for, encoded as a list of dictionaries with role and content.',
      items: {
        type: 'object',
        properties: {
          role: { type: 'string' },
          content: { type: 'string' },
        },
        required: ['role', 'content'],
      },
      minItems: 1,
    },
    response_format: {
      type: 'object',
      description: 'Object specifying the response format.',
    },
    tools: {
      type: ['array', 'null'],
      description: 'A list of tools the model may use. Each tool is represented as an object.',
      items: {
        type: 'object',
      },
    },
    tool_choice: {
      type: ['object', 'string'],
      description: "Controls which tool is called by the model. Default is 'auto'.",
    },
    presence_penalty: {
      type: 'number',
      description: 'Penalizes repetition of words or phrases to encourage diversity.',
      default: 0,
      minimum: -2,
      maximum: 2,
    },
    frequency_penalty: {
      type: 'number',
      description: 'Penalizes repetition of frequently appearing words to encourage diversity.',
      default: 0,
      minimum: -2,
      maximum: 2,
    },
    n: {
      type: ['integer', 'null'],
      description: 'Number of completions to return for each request. Input tokens are billed once.',
    },
    safe_prompt: {
      type: 'boolean',
      description: 'Whether to inject a safety prompt before all conversations.',
      default: false,
    },
  },
  required: ['model', 'messages'],
} as const;

// this confirms that the type is a valid JSON schema, but we want it as a `const` so we can use it to index the properties
export const mistralSchemaProperties: DeepReadonly<JSONSchema7> = mistralJsonSchema;

export const retrieveMistralParameters = (): string[] => Object.keys(mistralJsonSchema.properties);
